import React, { useEffect, useState } from 'react';
import {
    Box,
    useTheme,
    SelectChangeEvent,
    Typography,
    Popover,
    IconButton,
    CircularProgress,
} from '@mui/material';
import { Project } from '../../interfaces/TaskOverview';
import LocalStorageUtil from '../../utils/localStorageUtil';
import { storageKeys } from '../../utils/constants';
import apiService from '../../apiService/ApiService';
import { AxiosResponse, ProjectListingApi, SaveWorkerHoursLogging } from '../../utils/urlConst';
import { Category } from '../../screens/TaskOverview';
import { useAlert } from '../../contexts/AlertContext';
import { t } from 'i18next';
import { useNavigate } from 'react-router-dom';
import ProjectDropdownLog from './ProjectDropDownLog';
import { ProjectItem, useTimerContext } from '../../contexts/TimerContext';
import { Timer } from '@mui/icons-material';
import { formatTime } from '../../utils/commonMethods';

interface TimerMobilePopupProps {
    handleClose: () => void;
    elapsedTime: number;
    anchorEl?: HTMLButtonElement | null
}

const TimerMobilePopup: React.FC<TimerMobilePopupProps> = ({
    handleClose,
    elapsedTime,
    anchorEl
}) => {
    const theme = useTheme()
    const { isTimerOn, setStartTime, setIsTimerOn, selectedProjectForLog, setSelectedProjectForLog, selectedProjectIdForNavigation } = useTimerContext();
    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;
    const [projectList, setProjectList] = useState<Project[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [projectSelectedFromDropdown, setProjectSelectedFromDropdown] = useState<ProjectItem | null>(null);
    const { showAlert } = useAlert();
    const navigate = useNavigate();


    // fetch ProjectList api call
    const getProjectList = async () => {
        try {
            setIsLoading(true)
            let userToken = LocalStorageUtil.getItem(storageKeys.authToken, "");
            let userId = LocalStorageUtil.getItem(storageKeys.userId, "");
            let jsondata = JSON.stringify({
                token: userToken,
                user_id: userId,
            });

            apiService.setAuthToken(userToken);
            const responseJson: AxiosResponse | null = await apiService.post(ProjectListingApi, jsondata);
            if (responseJson?.hasOwnProperty('status') && responseJson?.status_code === 1) {
                if (responseJson?.data?.project_listing?.length) {
                    let categoryList = responseJson?.data?.project_listing.map((category: Category) => {
                        return {
                            ...category,
                        };
                    });
                    categoryList.sort((firstCategory: Category, secondCategory: Category) => firstCategory?.project_name.localeCompare(secondCategory?.project_name));

                    setProjectList(categoryList);
                }
            } else if (responseJson?.hasOwnProperty('status') && (responseJson?.status_code === 2 || responseJson?.status_code === 0)) {
                LocalStorageUtil.clear();
                navigate("/login");
            } else if (responseJson?.hasOwnProperty('status') && responseJson?.status_code === 3) {
                setProjectList([]);
                showAlert(t("taskOverView.no_projects_associated_with_worker"), "info")
                setIsLoading(false);
            } else {
                showAlert(t("common.server_error"), 'error')
                setIsLoading(false);
            }
        } catch (error) {
            showAlert(t("common.server_error"), 'error')
        } finally {
            setIsLoading(false);
        }
    };

    // fetch ProjectList api call
    const saveProjectLogToServer = async (projectId: number, startTime: string | null, endTime: string | null) => {
        try {
            setIsLoading(true)
            let userToken = LocalStorageUtil.getItem(storageKeys.authToken, "");
            let userId = LocalStorageUtil.getItem(storageKeys.userId, "");
            let medwerkerId = LocalStorageUtil.getItem(storageKeys.workerId, "");

            let jsondata = JSON.stringify({
                token: userToken,
                user_id: userId,
                medewerker_id: medwerkerId,
                project_id: projectId,
                start_time: startTime,
                end_time: endTime,
            });

            apiService.setAuthToken(userToken);
            const responseJson: AxiosResponse | null = await apiService.post(SaveWorkerHoursLogging, jsondata);
            if (responseJson?.hasOwnProperty('status') && responseJson?.status_code === 1) {
                showAlert(startTime ? "Project started successfully" : "Project logged Succesfully", "success")
            } else if (responseJson?.hasOwnProperty('status') && (responseJson?.status_code === 2 || responseJson?.status_code === 0)) {
                LocalStorageUtil.clear();
                navigate("/login");
            } else if (responseJson?.hasOwnProperty('status') && responseJson?.status_code === 3) {
                showAlert(t("taskOverView.no_projects_associated_with_worker"), "info")
                setIsLoading(false);
            } else {
                showAlert(t("common.server_error"), 'error')
                setIsLoading(false);
            }
        } catch (error) {
            showAlert(t("common.server_error"), 'error')
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveEndTime = () => {
        const projectId = selectedProjectForLog?.id ?? 0
        const now = new Date()
        const endTime = `${now.getHours()}:${now.getMinutes()}:${now.getSeconds()}`
        console.log("endTime", endTime)
        saveProjectLogToServer(projectId, null, endTime)
            .finally(() => {
                setSelectedProjectForLog({
                    id: null,
                    name: null,
                })
                setProjectSelectedFromDropdown(null);
                handleClose();
            }
            )
    }

    const handleSaveStartTime = (selectedProject: ProjectItem) => {
        const projectId = selectedProject.id ?? 0
        const now = new Date()
        const startTime = `${now.getHours()}:${now.getMinutes()}:${now.getSeconds()}`
        console.log("startTime", startTime)
        saveProjectLogToServer(projectId, startTime, null)
            .finally(() => {
                setProjectSelectedFromDropdown(null);
                handleClose();
            })
    }

    const getRecentSelectedProject = () => {
        var selectedProject: ProjectItem | null = null
        if (projectSelectedFromDropdown) {
            selectedProject = projectSelectedFromDropdown;
        } else if (selectedProjectForLog.id) {
            selectedProject = selectedProjectForLog
        } else if (selectedProjectIdForNavigation) {
            const item = projectList.filter((item) => item.id === selectedProjectIdForNavigation)
            if (item.length) {
                const selectedItem = item[0]
                selectedProject = { id: selectedItem.id, name: selectedItem.project_name };
            }
        }
        return selectedProject;
    }
    const startTimer = () => {
        const recentProject = getRecentSelectedProject()
        if (recentProject) {
            handleSaveStartTime(recentProject)
            setSelectedProjectForLog(recentProject);
        }
        setIsTimerOn(true);
    }

    const stopTimer = () => {
        setIsTimerOn(false)
        setStartTime(0)
        handleSaveEndTime()
    }

    // dropdown selection
    const handleProjectChange = async (event: SelectChangeEvent<string>) => {
        const selectedProject = JSON.parse(event.target.value);
        setProjectSelectedFromDropdown(selectedProject)
    };

    const getButtonStatus = () => {
        return (selectedProjectForLog?.id === null &&
            selectedProjectIdForNavigation === null
            && projectSelectedFromDropdown === null) || isLoading
    }
    useEffect(() => {
        // projectList fetch api call
        getProjectList();
    }, []);
    return (
        <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
        >
            <Box sx={{
                display: "flex",
                px: 2, py: 2,
                alignItems: "center",
                flexDirection: "column",
                justifyContent: "space-between",
                width: 300
            }}>
                {isLoading ?
                    <CircularProgress
                        sx={{ color: "primary", alignSelf: "center", verticalAlign: "true" }}
                    /> :
                    <Box sx={{ display: "flex", alignItems: "center", flexDirection: "column" }}>
                        <Typography
                            textAlign={"center"}
                            color='primary' sx={{
                                fontFamily: "Nunito Sans Regular500",
                                fontSize: "14px",
                                p: 1,
                                mb: 2
                            }}>{isTimerOn ? "Wilt u de timer voor dit project stoppen?" : "Kies je project:"}</Typography>
                        <ProjectDropdownLog
                            projectList={projectList}
                            handleProjectChange={handleProjectChange}
                        />
                    </Box>
                }

                <Box sx={{
                    flexDirection: "row",
                    display: "flex", alignItems: "center", mt: 2,

                }}>
                    {!isTimerOn ? (
                        <IconButton size="small"
                            onClick={startTimer}
                            disabled={getButtonStatus()}
                        >
                            <Timer fontSize="large" sx={{ color: (getButtonStatus()) ? "grey.400" : theme.palette.primary.main }} />
                        </IconButton>
                    ) :
                        (<IconButton size="small"
                            onClick={stopTimer}
                        // disabled={projectSelectedFromDropdown === null}
                        >
                            <Timer fontSize="large" sx={{ color: theme.palette.error.dark }} />
                        </IconButton>
                        )
                    }
                    <Typography sx={{
                        fontFamily: "Nunito Sans Bold",
                        fontSize: "28px",
                        color: theme.palette.blueColors.main
                    }}>{formatTime(elapsedTime)}</Typography>
                </Box>
            </Box>
        </Popover>
    );
};

export default TimerMobilePopup;